import {
  Edit as EditIcon,
  Folder as FilesIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Column,
  DataTable,
  makeColumnsSearchable,
} from "../../../components/common/DataTable/DataTable";
import { SearchableColumn } from "../../../components/common/SearchInputs";
import {
  DotInspectionResponseDto,
  DriverChargedStatus,
  DriverChargedStatusLabels,
} from "../../../types/dotInspection";
import { useDeleteDotInspectionMutation } from "../../../api/dotInspectionsApi";
import DotInspectionFilesDialog from "./DotInspectionFilesDialog";

interface DotInspectionsTableProps {
  inspections: DotInspectionResponseDto[];
  isLoading: boolean;
  totalCount?: number;
  hasNextPage?: boolean;
  isLoadingMore?: boolean;
  isLoadingRows?: boolean;
  onLoadMore?: () => void;
  enableSearch?: boolean;
  onSearchChange?: (filters: Record<string, any>) => void;
}

const getDriverChargedColor = (status: DriverChargedStatus) => {
  switch (status) {
    case DriverChargedStatus.DriverCharged:
      return "error";
    case DriverChargedStatus.PendingCharge:
      return "warning";
    case DriverChargedStatus.NoCharge:
      return "success";
    case DriverChargedStatus.InvoicedOnDriver:
      return "info";
    default:
      return "default";
  }
};

const DotInspectionsTable: React.FC<DotInspectionsTableProps> = ({
  inspections,
  isLoading,
  totalCount,
  hasNextPage,
  isLoadingMore,
  isLoadingRows,
  onLoadMore,
  enableSearch = false,
  onSearchChange,
}) => {
  const navigate = useNavigate();
  const [filesDialogOpen, setFilesDialogOpen] = useState(false);
  const [selectedInspectionId, setSelectedInspectionId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DotInspectionResponseDto | null>(null);
  const [deleteInspection, { isLoading: isDeleting }] = useDeleteDotInspectionMutation();

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await deleteInspection(deleteTarget.id);
    setDeleteTarget(null);
  };

  const truncated = (value: string | null, maxLen = 60) => {
    if (!value) return <Typography sx={{ fontSize: "0.8125rem" }}>-</Typography>;
    const text = value.length > maxLen ? value.slice(0, maxLen) + "…" : value;
    return (
      <Tooltip title={value.length > maxLen ? value : ""} placement="top" arrow>
        <Typography sx={{ fontSize: "0.8125rem" }}>{text}</Typography>
      </Tooltip>
    );
  };

  const formatAmount = (value: number | null) =>
    value != null
      ? `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : "-";

  const columns: Column[] = [
    {
      field: "inspectionDate",
      headerName: "Date",
      width: 110,
      renderCell: (value: string) => (
        <Typography sx={{ fontSize: "0.8125rem" }}>
          {value ? new Date(value).toLocaleDateString() : "-"}
        </Typography>
      ),
    },
    {
      field: "companyName",
      headerName: "Company",
      width: 140,
      renderCell: (value: string) => (
        <Typography sx={{ fontSize: "0.8125rem", fontWeight: 500 }}>
          {value || "-"}
        </Typography>
      ),
    },
    {
      field: "driverName",
      headerName: "Driver",
      width: 140,
      renderCell: (value: string) => (
        <Typography sx={{ fontSize: "0.8125rem" }}>{value || "-"}</Typography>
      ),
    },
    {
      field: "truckName",
      headerName: "Truck",
      width: 100,
      renderCell: (value: string) => (
        <Typography sx={{ fontSize: "0.8125rem" }}>{value || "-"}</Typography>
      ),
    },
    {
      field: "trailerName",
      headerName: "Trailer",
      width: 100,
      renderCell: (value: string) => (
        <Typography sx={{ fontSize: "0.8125rem" }}>{value || "-"}</Typography>
      ),
    },
    {
      field: "points",
      headerName: "Points",
      width: 90,
      renderCell: (value: number | null) => (
        <Typography sx={{ fontSize: "0.8125rem" }}>
          {value != null ? value : "-"}
        </Typography>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1.5,
      renderCell: (value: string) => truncated(value, 70),
    },
    {
      field: "amount",
      headerName: "Amount",
      width: 110,
      renderCell: (value: number | null) => (
        <Typography sx={{ fontSize: "0.8125rem", fontWeight: 500 }}>
          {formatAmount(value)}
        </Typography>
      ),
    },
    {
      field: "driverCharged",
      headerName: "Driver Charged",
      width: 135,
      renderCell: (value: DriverChargedStatus | null) =>
        value != null ? (
          <Chip
            label={DriverChargedStatusLabels[value] ?? "Unknown"}
            size="small"
            color={getDriverChargedColor(value)}
            variant="outlined"
            sx={{ fontSize: "0.75rem" }}
          />
        ) : (
          <Typography sx={{ fontSize: "0.8125rem" }}>-</Typography>
        ),
    },
  ];

  const getActions = (inspection: DotInspectionResponseDto) => [
    {
      icon: <EditIcon fontSize="small" />,
      label: "Edit",
      onClick: () => navigate(`/dot-inspections/${inspection.id}/edit`),
      color: "primary" as const,
    },
    {
      icon: <FilesIcon fontSize="small" />,
      label: "View Files",
      onClick: () => {
        setSelectedInspectionId(inspection.id);
        setFilesDialogOpen(true);
      },
      color: "info" as const,
    },
    {
      icon: <DeleteIcon fontSize="small" />,
      label: "Delete",
      onClick: () => setDeleteTarget(inspection),
      color: "error" as const,
    },
  ];

  const driverChargedSelectOptions = Object.entries(DriverChargedStatusLabels).map(
    ([value, label]) => ({
      value: Number(value),
      label,
    })
  );

  const searchableColumns: SearchableColumn[] = [
    ...makeColumnsSearchable(
      columns,
      ["companyName", "driverName", "truckName", "trailerName"],
      {
        companyName: "string",
        driverName: "string",
        truckName: "string",
        trailerName: "string",
      }
    ),
    {
      field: "driverCharged",
      headerName: "Driver Charged",
      searchable: true,
      searchType: "select",
      selectOptions: driverChargedSelectOptions,
    },
  ];

  return (
    <>
      <DataTable<DotInspectionResponseDto>
        items={inspections}
        columns={columns}
        actions={getActions}
        isLoading={isLoading}
        title="DOT Inspections"
        totalCount={totalCount}
        hasNextPage={hasNextPage}
        isLoadingMore={isLoadingMore}
        isLoadingRows={isLoadingRows}
        onLoadMore={onLoadMore}
        enableSearch={enableSearch}
        searchableColumns={searchableColumns}
        onSearchChange={onSearchChange}
      />

      {selectedInspectionId && (
        <DotInspectionFilesDialog
          open={filesDialogOpen}
          onClose={() => {
            setFilesDialogOpen(false);
            setSelectedInspectionId(null);
          }}
          inspectionId={selectedInspectionId}
        />
      )}

      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete DOT Inspection</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete DOT inspection{" "}
            <strong>#{deleteTarget?.id}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DotInspectionsTable;
